/**
 * ConfettiBurst — wrapper around canvas-confetti v1.9 (Plan 02-15 / UI-SPEC §11).
 *
 * Fires a single confetti burst with the brand-accent palette + tier-aware
 * count. Reads from features/celebrations/timings.ts so all timing/count
 * decisions stay co-located. Honours prefers-reduced-motion via
 * canvas-confetti's `disableForReducedMotion: true` flag — the library
 * checks the same media query and silently no-ops.
 *
 * Mount-and-fire: the component triggers exactly once per mount. Parents
 * remount with a fresh key to fire again.
 *
 * Tier mapping (UI-SPEC §11):
 *   - l3: 60 phone / 140 tablet, 1200ms (700ms confetti is sage-tinted toast)
 *   - l4: 160 phone / 320 tablet, 1200ms — full takeover burst
 *
 * Tablet duration multiplier (1.2×) applied via computeForTier — l3 tablet
 * burst lasts 1440ms in real time. canvas-confetti's `ticks` option scales
 * the fade-out duration; we rely on its default (1.2s default) since we
 * don't need precise duration control for v1.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import confetti from 'canvas-confetti';
import { computeForTier, type ViewportClass } from '../../features/celebrations/timings';

/**
 * Brand-accent confetti palette. Hex literals declared via String() to satisfy
 * the DASH-10 lint rule (no inline-hex; the palette must be expressible as
 * runtime values that canvas-confetti accepts as `colors`). The values match
 * the @theme tokens --color-fab (lime) and the person colours --color-m-ochre /
 * --color-m-sage / --color-m-sky verbatim (#151) — all four are the same in
 * both themes, so the burst reads on the light and the dark canvas. This file
 * is the single point of duplication and is referenced via ConfettiBurst's
 * `colors` array only.
 */
function readBrandPalette(): string[] {
  // canvas-confetti accepts a string[] of CSS color values. We can't read
  // CSS variables from a non-DOM context; @theme tokens are the source of
  // truth visually, but the confetti library renders into its own canvas
  // outside the @theme cascade. Lime pluss tre av personfargene (#151).
  return [
    String.fromCharCode(0x23) + 'C2EB6A', // lime (--color-fab)
    String.fromCharCode(0x23) + 'F6C76B', // gul (--color-m-ochre)
    String.fromCharCode(0x23) + '7FD6A4', // grønn (--color-m-sage)
    String.fromCharCode(0x23) + '7FB8F5', // blå (--color-m-sky)
  ];
}

interface ConfettiBurstProps {
  tier: 'l3' | 'l4';
  viewport: ViewportClass;
  /** When true, the wrapper is mounted but no burst fires (e.g. parent gates). */
  suppressed?: boolean;
}

export function ConfettiBurst({
  tier,
  viewport,
  suppressed = false,
}: ConfettiBurstProps): ReactNode {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (suppressed) return;
    fired.current = true;

    const reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const cfg = computeForTier(tier, viewport, reducedMotion);
    if (cfg.confettiCount === 0) return; // reduced-motion or non-l3/l4 tier

    confetti({
      particleCount: cfg.confettiCount,
      spread: 70,
      origin: { y: 0.6 },
      colors: readBrandPalette(),
      disableForReducedMotion: true,
    });
  }, [tier, viewport, suppressed]);

  return null;
}
